import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { isLoggedIn, logout } from '../api/auth';
import { ChildProfile, getActiveProfile, getCachedActiveProfile } from '../api/profiles';
import ChildProfileBadge from '../components/ChildProfileBadge';

type Props = NativeStackScreenProps<RootStackParamList, 'Settings'>;

export default function SettingsScreen({ navigation }: Props) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [profile, setProfile] = useState<ChildProfile | null>(() => getCachedActiveProfile());

  useEffect(() => {
    const loadAccount = () => {
      const hasSession = isLoggedIn();
      setLoggedIn(hasSession);

      if (!hasSession) {
        setProfile(null);
        return;
      }

      const cached = getCachedActiveProfile();
      if (cached) setProfile(cached);

      getActiveProfile().then(setProfile).catch(() => setProfile(null));
    };

    loadAccount();
    const unsubscribe = navigation.addListener('focus', loadAccount);
    return unsubscribe;
  }, [navigation]);

  const handleLogout = async () => {
    await logout();
    setLoggedIn(false);
    setProfile(null);
    navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
  };

  const settingItems: Array<{ label: string; value: string; icon: keyof typeof Ionicons.glyphMap }> = [];

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <ActionHeader onBack={() => navigation.goBack()} rightIcons={['ellipsis-horizontal']} />

        <Text style={styles.title}>Cài đặt</Text>
        <Text style={styles.subtitle}>Quản lý tài khoản, trải nghiệm nghe và tùy chọn ứng dụng.</Text>

        {!loggedIn ? (
          <View style={styles.loginCard}>
            <View style={styles.loginIcon}>
              <Ionicons name="person" size={26} color={colors.buttonText} />
            </View>
            <Text style={styles.loginTitle}>Bạn chưa đăng nhập</Text>
            <Text style={styles.loginText}>Đăng nhập để lưu thư viện, lịch sử nghe và hồ sơ của bé.</Text>
            <Pressable style={styles.loginButton} onPress={() => navigation.navigate('Login')}>
              <Text style={styles.loginButtonText}>Đăng nhập</Text>
            </Pressable>
          </View>
        ) : (
          <View style={styles.profileSection}>
            <ChildProfileBadge profile={profile} subtitle="Đã đăng nhập" onLogout={handleLogout} />
          </View>
        )}

        <Text style={styles.sectionTitle}>Tài khoản</Text>
        <View style={styles.panel}>
          <Pressable style={styles.rowItem} onPress={() => loggedIn ? navigation.navigate('ProfileSelect') : navigation.navigate('Login')}>
            <View style={styles.rowLeft}>
              <Ionicons name="person-circle-outline" size={20} color={colors.button} />
              <Text style={styles.rowLabel}>{loggedIn ? 'Đổi hồ sơ bé' : 'Đăng nhập để đồng bộ thư viện'}</Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
          </Pressable>
          {loggedIn ? (
            <Pressable style={styles.rowItem} onPress={handleLogout}>
              <View style={styles.rowLeft}>
                <Ionicons name="log-out-outline" size={20} color={colors.button} />
                <Text style={styles.rowLabel}>Thoát tài khoản</Text>
              </View>
              <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
            </Pressable>
          ) : null}
        </View>

        {settingItems.length ? (
          <>
            <Text style={styles.sectionTitle}>Ứng dụng</Text>
            <View style={styles.panel}>
              {settingItems.map((item) => (
                <Pressable key={item.label} style={styles.rowItem}>
                  <View style={styles.rowLeft}>
                    <Ionicons name={item.icon} size={20} color={colors.blue} />
                    <Text style={styles.rowLabel}>{item.label}</Text>
                  </View>
                  <View style={styles.rowRight}>
                    <Text style={styles.rowValue}>{item.value}</Text>
                    <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                  </View>
                </Pressable>
              ))}
            </View>
          </>
        ) : null}

        <Text style={styles.version}>Phiên bản 1.0.0</Text>
      </ScrollView>

      <BottomTabBar
        active="Settings"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => null}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingTop: 62,
    paddingHorizontal: 24,
    paddingBottom: 124,
  },
  title: {
    color: colors.textPrimary,
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 8,
  },
  subtitle: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 22,
  },
  loginCard: {
    alignItems: 'center',
    backgroundColor: colors.panel,
    borderRadius: 26,
    padding: 22,
    marginBottom: 24,
  },
  loginIcon: {
    width: 58,
    height: 58,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.button,
    marginBottom: 14,
  },
  loginTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 8,
  },
  loginText: {
    color: colors.textSecondary,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    marginBottom: 18,
  },
  loginButton: {
    height: 46,
    minWidth: 160,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.button,
  },
  loginButtonText: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '900',
  },
  profileSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 10,
  },
  panel: {
    backgroundColor: colors.panel,
    borderRadius: 22,
    paddingVertical: 6,
    marginBottom: 22,
  },
  rowItem: {
    minHeight: 56,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  rowLabel: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '800',
  },
  rowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  rowValue: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '700',
  },
  version: {
    color: colors.textSecondary,
    fontSize: 12,
    textAlign: 'center',
  },
});
